import { PlanType } from "@brookbush/database";
import { CheckoutStatus, UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { Dialog, DialogPanel } from "@headlessui/react";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRightIcon, Loader2Icon, XIcon } from "lucide-react";
import posthog from "posthog-js";
import { memo, useEffect, useRef } from "react";

import MembershipFeatures from "../MembershipFeatures";
import BecomeAMemberModalPlanSelector from "./BecomeAMemberModalPlanSelector";
import BecomeAMemberSaleCounter from "./BecomeAMemberSaleCounter";
import { useAccess, useUserAction } from "~/hooks";
import useCheckout from "~/hooks/useCheckout";
import useConversionFlag from "~/hooks/useConversionFlag";
import useJoiningPrice from "~/hooks/useJoiningPrice";
import useOnceInView from "~/hooks/useOnceInView";
import usePlanConfig from "~/hooks/usePlanConfig";
import useSale from "~/hooks/useSale";
import captureBecomeAMemberEvent from "~/lib/captureBecomeAMemberEvent";
import {
	CONVERSION_FLAGS,
	isConversionFlagOn,
	resolveMobilePaymentFirstVariant,
} from "~/lib/conversion-test-flags";
import {
	MEMBERSHIP_MODAL_FIRST_SCREEN_FLAG,
	resolveMembershipModalFirstScreenVariant,
} from "~/lib/membership-modal-experiment";
import { shouldResetModalPlanToIndividual } from "~/lib/membership-plan-defaults";
import {
	getSignupModalLayout,
	getSignupModalViewport,
} from "~/lib/signup-modal.lib";
import {
	resolveSignupModalDenseMobileVariant,
	SIGNUP_MODAL_DENSE_MOBILE_FLAG,
} from "~/lib/signup-modal-dense-experiment";
import { shouldDismissMembershipModal } from "~/lib/stripe/checkoutPaymentGuards";
import BecomeAMemberBanner from "~/pages/become-a-member/components/BecomeAMemberBanner";
import BecomeAMemberCheckout from "~/pages/become-a-member/components/BecomeAMemberCheckout";
import BecomeAMemberCheckoutSuccess from "~/pages/become-a-member/components/BecomeAMemberCheckoutSuccess";
import BecomeAMemberResume from "~/pages/become-a-member/components/BecomeAMemberResume";
import BecomeAMemberEndTrial from "~/pages/become-a-member/components/BecomeMemberEndTrial";

/** Ignore backdrop/Escape closes briefly so the opening click cannot dismiss the modal. */
const BACKDROP_CLOSE_GRACE_MS = 400;

const BecomeAMemberModal = () => {
	const { userAction, resetUserAction } = useUserAction();
	const isOpen = userAction === UserAction.BECOME_A_MEMBER;
	const { isLoaded } = useAuth();

	const { checkoutStatus, isPaymentSubmitting } = useCheckout();

	const { planConfig, setPlanType, isLoadingPlanConfig } = usePlanConfig();
	const { sale, isLoadingSale } = useSale();
	const { isPaused, isTrialing } = useAccess();

	const firstScreenVariant = resolveMembershipModalFirstScreenVariant(
		posthog.getFeatureFlag(MEMBERSHIP_MODAL_FIRST_SCREEN_FLAG),
	);
	const isFirstScreenTest = firstScreenVariant === "test";

	const denseVariant = resolveSignupModalDenseMobileVariant(
		posthog.getFeatureFlag(SIGNUP_MODAL_DENSE_MOBILE_FLAG),
	);
	const viewport = getSignupModalViewport();
	const denseLayout = getSignupModalLayout(viewport, denseVariant);
	const isDenseMobile = viewport === "mobile" && denseVariant === "test";

	useEffect(() => {
		if (!shouldResetModalPlanToIndividual(isOpen, planConfig.planType)) return;

		setPlanType(PlanType.INDIVIDUAL);
	}, [isOpen, planConfig.planType, setPlanType]);

	const isShown = isOpen && isLoaded;

	const { value: paymentFirstFlag, ready: isPaymentFirstReady } =
		useConversionFlag(CONVERSION_FLAGS.mobilePaymentFirst, {
			enabled: isShown && viewport === "mobile",
		});
	const paymentFirstVariant =
		resolveMobilePaymentFirstVariant(paymentFirstFlag);
	const isPaymentFirst =
		viewport === "mobile" && paymentFirstVariant === "test";
	const planColumnFirst = isDenseMobile || isFirstScreenTest || isPaymentFirst;
	const isConfigReady =
		!isLoadingSale &&
		!isLoadingPlanConfig &&
		!!planConfig.price &&
		(viewport !== "mobile" || isPaymentFirstReady);

	const { offer: joiningOffer, ready: isJoiningPriceReady } = useJoiningPrice({
		enabled: isShown,
	});

	const hasCapturedOpen = useRef(false);
	const hasClosedRef = useRef(false);
	const backdropCloseableAtRef = useRef(0);
	const wasWaitingForConfigRef = useRef(false);

	useEffect(() => {
		if (!isShown) {
			hasCapturedOpen.current = false;
			hasClosedRef.current = false;
			backdropCloseableAtRef.current = 0;
			wasWaitingForConfigRef.current = false;
			return;
		}

		backdropCloseableAtRef.current = Date.now() + BACKDROP_CLOSE_GRACE_MS;
	}, [isShown]);

	// When the spinner shell swaps to the full modal, Headless may fire onClose
	// on unmount — restart the grace window and ignore backdrop until then.
	useEffect(() => {
		if (!isShown) return;

		if (!isConfigReady) {
			wasWaitingForConfigRef.current = true;
			return;
		}

		if (wasWaitingForConfigRef.current) {
			wasWaitingForConfigRef.current = false;
			backdropCloseableAtRef.current = Date.now() + BACKDROP_CLOSE_GRACE_MS;
		}
	}, [isShown, isConfigReady]);

	useEffect(() => {
		if (
			!isShown ||
			isPaused ||
			checkoutStatus === CheckoutStatus.SUCCESSFUL ||
			!isConfigReady ||
			!isJoiningPriceReady ||
			hasCapturedOpen.current
		) {
			return;
		}

		const price = planConfig.price;
		if (!price) return;

		hasCapturedOpen.current = true;

		captureBecomeAMemberEvent(
			"opened",
			{
				surface: "modal",
				interval: price.recurring?.interval,
				promo_code: planConfig.promoCode?.code,
				price_id: price.id,
				density: denseVariant,
				layout: denseLayout,
				payment_first: viewport === "mobile" ? paymentFirstVariant : undefined,
			},
			joiningOffer,
		);
	}, [
		checkoutStatus,
		denseLayout,
		denseVariant,
		paymentFirstVariant,
		viewport,
		isConfigReady,
		isJoiningPriceReady,
		isPaused,
		isShown,
		joiningOffer,
		planConfig.price,
		planConfig.promoCode?.code,
	]);

	const planSelectorRef = useRef<HTMLDivElement>(null);
	const { value: trackingFlag } = useConversionFlag(
		CONVERSION_FLAGS.conversionTracking,
		{ enabled: isShown },
	);
	useOnceInView(
		planSelectorRef,
		() =>
			captureBecomeAMemberEvent("plans_viewed", {
				surface: "modal",
				density: denseVariant,
				layout: denseLayout,
				payment_first: viewport === "mobile" ? paymentFirstVariant : undefined,
			}),
		isShown && isConfigReady && isConversionFlagOn(trackingFlag),
	);

	const closeModal = (reason: "x" | "not_interested" | "backdrop") => {
		if (!shouldDismissMembershipModal(isPaymentSubmitting)) return;
		if (hasClosedRef.current) return;

		if (reason === "backdrop") {
			// Spinner shell → full modal swap, or click-through from the CTA.
			if (!isConfigReady || Date.now() < backdropCloseableAtRef.current) {
				return;
			}
		}

		hasClosedRef.current = true;
		captureBecomeAMemberEvent("closed", {
			surface: "modal",
			reason,
			density: denseVariant,
			layout: denseLayout,
		});
		resetUserAction();
	};

	return (
		<AnimatePresence mode="wait">
			{isShown &&
				(isPaused && isConfigReady ? (
					<BecomeAMemberResume isModal />
				) : checkoutStatus === CheckoutStatus.SUCCESSFUL ? (
					<BecomeAMemberCheckoutSuccess isModal />
				) : !isConfigReady ? (
					<Dialog onClose={() => closeModal("backdrop")} open={isShown} static>
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1, transition: { duration: 0.3 } }}
							exit={{ opacity: 0, transition: { duration: 0.2 } }}
							className="fixed inset-0 z-50 bg-black/40 dark:bg-black/50"
							aria-hidden="true"
							key="become-a-member-modal-loading-backdrop"
						/>

						<div className="fixed inset-0 z-50 flex items-start tablet:items-center justify-center overflow-auto">
							<DialogPanel
								as={motion.div}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0, transition: { duration: 0.25 } }}
								exit={{ opacity: 0, y: 10, transition: { duration: 0.15 } }}
								key="become-a-member-modal-loading"
								className="relative flex h-[min(24rem,90vh)] w-full max-w-md flex-col items-center justify-center overflow-hidden rounded-none tablet:rounded-xl bg-white shadow-black/5 shadow-xl dark:bg-slate-800 dark:shadow-black/30"
							>
								<button
									type="button"
									aria-label="Close"
									className="absolute top-3.5 right-4 z-50 flex cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
									onClick={() => closeModal("x")}
								>
									<XIcon className="size-5" />
								</button>

								<Loader2Icon
									aria-hidden
									className="size-8 animate-spin text-slate-400 dark:text-slate-500"
								/>
								<span className="sr-only">Loading membership plans</span>
							</DialogPanel>
						</div>
					</Dialog>
				) : (
					<Dialog onClose={() => closeModal("backdrop")} open={isShown} static>
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1, transition: { duration: 0.3 } }}
							exit={{ opacity: 0, transition: { duration: 0.2 } }}
							className="fixed inset-0 z-50 bg-black/40 dark:bg-black/50"
							aria-hidden="true"
							key="become-a-member-modal-backdrop"
						/>

						<div
							className={clsx(
								"fixed inset-0 z-50 flex items-start tablet:items-center justify-center overflow-auto",
							)}
						>
							<DialogPanel
								as={motion.div}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0, transition: { duration: 0.25 } }}
								exit={{ opacity: 0, y: 10, transition: { duration: 0.15 } }}
								key="signup-modal"
								className={clsx(
									"relative flex w-full tablet:flex-row flex-col justify-start rounded-none tablet:rounded-xl bg-white shadow-black/5 shadow-xl dark:bg-slate-800 dark:shadow-black/30",
									isDenseMobile
										? "max-h-[100dvh] overflow-y-auto"
										: "overflow-hidden",
									sale
										? "tablet:h-[45rem] tablet:max-h-[95vh] tablet:max-w-4xl"
										: "tablet:h-[39rem] tablet:max-w-4xl",
								)}
							>
								<button
									type="button"
									aria-label="Close"
									className={clsx(
										"fixed tablet:absolute right-4 z-50 flex cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600",
										isDenseMobile ? "top-2.5" : "top-3.5",
									)}
									onClick={() => closeModal("x")}
								>
									<XIcon className="size-5" />
								</button>

								<div
									className={clsx(
										"h-auto tablet:w-1/2 tablet:border-r border-b tablet:border-b-0",
										planColumnFirst && "order-2 tablet:order-1",
									)}
								>
									<MembershipFeatures
										variant={firstScreenVariant}
										density={isDenseMobile ? "compact" : "default"}
									/>
								</div>

								<div
									className={clsx(
										"flex tablet:w-1/2 flex-col items-center",
										isDenseMobile
											? "gap-3 px-3 pt-3 pb-4"
											: "tablet:overflow-auto tablet:p-10 px-6 pt-6 pb-10",
										planColumnFirst && "order-1 tablet:order-2",
									)}
								>
									{isTrialing ? (
										<div
											className={clsx(
												"flex w-full max-w-md flex-col",
												isDenseMobile ? "gap-3" : "gap-4",
											)}
										>
											<div
												className={clsx(
													"flex flex-col text-center",
													isDenseMobile ? "gap-1" : "gap-2",
												)}
											>
												<h2
													className={clsx(
														"font-bold",
														isDenseMobile ? "text-xl" : "text-2xl",
													)}
												>
													Your Trial is Ending Soon
												</h2>

												<p>Cancel anytime. 30-day money-back guarantee.</p>
											</div>
											<BecomeAMemberEndTrial />
										</div>
									) : (
										<div
											className={clsx(
												"flex w-full max-w-md flex-col",
												isDenseMobile ? "gap-3" : "gap-4",
												{
													"my-auto": !!sale && !isDenseMobile,
												},
											)}
										>
											<BecomeAMemberSaleCounter />

											<div
												className={clsx(
													"flex flex-col text-center",
													isDenseMobile ? "gap-1" : "gap-2",
												)}
											>
												<h2
													className={clsx(
														"font-bold",
														isDenseMobile ? "text-xl" : "text-2xl",
													)}
												>
													{sale?.isAnnualUpgrade
														? "Upgrade Your Plan"
														: "Choose Your Plan"}
												</h2>

												<p>Cancel anytime. 30-day money-back guarantee.</p>
											</div>

											<div ref={planSelectorRef} className="w-full">
												<BecomeAMemberModalPlanSelector />
											</div>

											<BecomeAMemberBanner />

											<BecomeAMemberCheckout isModal />

											<button
												type="button"
												onClick={() => closeModal("not_interested")}
												className={clsx(
													"mx-auto w-fit hover:underline",
													isDenseMobile ? "mt-2" : "mt-4",
													isDenseMobile || isFirstScreenTest
														? "text-slate-500 text-sm dark:text-slate-400"
														: "group flex items-center justify-center gap-1 font-medium",
												)}
											>
												Not Interested
												{!(isDenseMobile || isFirstScreenTest) && (
													<>
														{" "}
														<ChevronRightIcon
															strokeWidth={2.35}
															className="size-5 transition group-hover:translate-x-0.5"
														/>
													</>
												)}
											</button>
										</div>
									)}
								</div>
							</DialogPanel>
						</div>
					</Dialog>
				))}
		</AnimatePresence>
	);
};

export default memo(BecomeAMemberModal);
